/* eslint-disable no-param-reassign */

import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'

import { inArray } from '@/scripts/Utils'

import { ignoreStateReset, getInitialState } from '@/initialState'

Vue.use(Vuex)

export default new Vuex.Store({
  plugins: [
    new VuexPersist({
      key: 'algolia-onboarding-tutorial',
      storage: window.localStorage,
      reducer(state) {
        return {
          dataset: state.dataset,
          indexingLanguage: state.indexingLanguage,
          uiLibrary: state.uiLibrary
        }
      }
    }).plugin
  ],
  state: getInitialState(),
  getters: {
    getUserId(state) {
      return state.userId || false
    },
    getAppId(state) {
      return state.applicationId || state.guestAppId
    },
    getApiKey(state) {
      return state.writeApiKey || false
    },
    getSearchApiKey(state) {
      return state.searchApiKey || state.guestAPIKey
    },
    getCredentials(state, getters) {
      return [getters.getAppId, getters.getApiKey, getters.getIndexName]
    },
    isUserLoggedIn(state, getters) {
      return (
        Boolean(getters.getUserId) &&
        Boolean(getters.getApiKey) &&
        Boolean(getters.getAppId) &&
        Boolean(getters.getSearchApiKey)
      )
    },
    getPickDatasetsDataset(state) {
      return state.datasets.pickDataset[state.dataset]
    },
    getPushDataDataset(state) {
      return state.datasets.pushData[state.indexingLanguage]
    },
    getSetRelevanceDataset(state) {
      return state.datasets.setRelevance[state.indexingLanguage]
    },
    getUiLibrary(state) {
      return state.uiLibraries[state.uiLibrary]
    },
    getCodeSandboxUrl(state, getters) {
      const uiLibraryId = getters.getUiLibrary.id
      const datasetId = getters.getPickDatasetsDataset.id
      return `/github/algolia/doc-code-samples/tree/master/onboarding/demos/${uiLibraryId}/${datasetId}`
    },
    getCodeSandboxQueryString(state, getters) {
      const modulePath = getters.getUiLibrary.codeSandboxModulePath
      const forceRefresh = getters.getUiLibrary.forceRefresh
      return `?codemirror=1&fontsize=13&view=split&hidenavigation=1&module=${modulePath}&forcerefresh=${forceRefresh}`
    },
    getLastVisitedStepNumber(state) {
      const index = state.steps
        .slice()
        .reverse()
        .findIndex(step => step.visited)
      return (
        state.steps.length - (index !== -1 ? index : state.steps.length - 1)
      )
    },
    getIndexName(state, getters) {
      return `demo_${getters.getPickDatasetsDataset.id}`
    },
    getDatasetSettings() {
      return datasetName => {
        const settings = {
          ecommerce: {
            searchableAttributes: [
              'brand',
              'name',
              'categories',
              'description'
            ],
            customRanking: ['desc(popularity)'],
            attributesForFaceting: ['categories', 'searchable(brand)', 'price']
          },
          saas: {
            searchableAttributes: [
              'Name',
              'Owner',
              'Account',
              'Email',
              'Website'
            ],
            customRanking: ['desc(Amount)'],
            attributesForFaceting: ['type'],
            attributeForDistinct: 'type'
          },
          media: {
            searchableAttributes: [
              'post_title',
              'post_author.display_name',
              'taxonomies.category',
              'content'
            ],
            customRanking: ['desc(post_date)', 'desc(record_index)'],
            attributesForFaceting: ['taxonomies.category'],
            attributeForDistinct: 'post_id'
          },
          geo: {
            searchableAttributes: ['country', 'city', 'name', 'airport_id'],
            customRanking: ['desc(nb_airline_liaisons)'],
            attributesForFaceting: ['city', 'country']
          }
        }

        return settings[datasetName]
      }
    },
    getDatasetQueryRules() {
      return datasetName => {
        const queryRules = {
          geo: [
            {
              objectID: 'country',
              condition: {
                pattern: '{facet:country}',
                anchoring: 'contains'
              },
              consequence: {
                params: {
                  aroundLatLngViaIP: false
                }
              }
            },
            {
              objectID: 'city',
              condition: {
                pattern: '{facet:city}',
                anchoring: 'contains'
              },
              consequence: {
                params: {
                  aroundLatLngViaIP: false
                }
              }
            }
          ]
        }

        return queryRules[datasetName]
      }
    }
  },
  mutations: {
    setUserData(state, payload) {
      state.userId = payload.userId
      state.applicationId = payload.applicationId
      state.searchApiKey = payload.searchApiKey
      state.writeApiKey = payload.writeApiKey
    },
    setStepAsVisited(state, payload) {
      state.steps[payload.step].visited = true
    },
    setStepAsCompleted(state, payload) {
      state.steps[payload.step].visited = true
      state.steps[payload.step].completed = true
    },
    setDataset(state, payload) {
      state.dataset = payload.index
    },
    setIndexingLanguage(state, payload) {
      state.indexingLanguage = payload.index
    },
    setUiLibrary(state, payload) {
      state.uiLibrary = payload.index
    },
    openCodeSandbox(state) {
      state.openCodeSandbox = true
    },
    closeCodeSandbox(state) {
      state.openCodeSandbox = false
    },
    setUploadedRecordsAmount(state, payload) {
      state.uploadedRecordsAmount = payload.amount
    },
    resetState(state) {
      const newState = getInitialState()
      Object.keys(newState).forEach(key => {
        if (!inArray(key, ignoreStateReset)) {
          if (Array.isArray(newState[key])) {
            state[key].forEach((item, index) => {
              Vue.set(state[key], index, newState[key][index])
            })
          } else {
            state[key] = newState[key]
          }
        }
      })
    }
  }
})
